/* eslint-disable import/prefer-default-export */
import { useState, useCallback, useMemo, useEffect } from "react";
import useSWR, { useSWRConfig } from "swr";
import { DistinctValue } from "../../types";
import { CLEANING_DATA_KEY } from "../../hooks/use-cleaning-data";
import useDragDropHook from "../../hooks/use-drag-drop";
import { useDataCleaningContext } from "../../context/data-cleaning-context";
import { getDistinctValues } from "../../services/cleaning-data-service";
import useClients from "../../../client-config/data/use-clients";
import useCategoryCalculations from "./hooks/use-category-calculations";
import useCheckboxState from "./hooks/use-checkbox-state";
import transformDistinctValuesResponse from "./utils/transform-distinct-values";
import buildDistinctValuesRequest from "./utils/build-distinct-values-request";

/**
 * Custom hook for managing distinct values state and logic.
 * Handles value categorization, selection, drag & drop, and menu state.
 */
export const useDistinctValues = () => {
  const { selection } = useDataCleaningContext();
  const { data: clientsData, isLoading: isLoadingClients } = useClients();
  const { mutate: mutateCache } = useSWRConfig();
  const [distinctValues, setDistinctValues] = useState<DistinctValue[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(1); // Default to Valid Values
  const [selectedTile, setSelectedTile] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<DistinctValue | null>(null);
  const [anchorEl, setAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [chartDrawerOpen, setChartDrawerOpen] = useState(false);
  const [chartItem, setChartItem] = useState<DistinctValue | null>(null);

  // Track manually moved values to persist after data refresh
  // Use value string as key (not ID) since IDs change when category/index changes
  const [manualMoves, setManualMoves] = useState<
    Map<string, { categoryId: number; timestamp: number }>
  >(new Map());

  // Reset state when field changes and auto-select first tile (ALL)
  useEffect(() => {
    setDistinctValues([]);
    setSelectedItem(null);
    setSelectedCategory(1); // Reset to Valid Values category
    setManualMoves(new Map()); // Clear manual moves when field changes

    // Auto-select tile 0 (All) immediately to start fetching distinct values
    if (selection.selectedFieldId) {
      setSelectedTile(0);
    } else {
      setSelectedTile(null);
    }
  }, [selection.selectedFieldId]);

  // Reset selected item when category changes
  useEffect(() => {
    setSelectedItem(null);
  }, [selectedCategory]);

  // Build the request body for distinct values API
  const requestBody = useMemo(
    () =>
      buildDistinctValuesRequest(
        selectedTile,
        clientsData,
        selection.selectedClientIds,
        isLoadingClients,
      ),
    [selectedTile, clientsData, selection.selectedClientIds, isLoadingClients],
  );

  // Fetch distinct values from API when a tile is selected
  const {
    data,
    error,
    isLoading: isLoadingDistinctValues,
    mutate,
  } = useSWR(
    requestBody && selection.selectedFieldId
      ? `/cleaning/field/${selection.selectedFieldId}/distinct-values?filter=${selectedTile}`
      : null,
    () => {
      if (requestBody && selection.selectedFieldId) {
        return getDistinctValues(selection.selectedFieldId, requestBody);
      }
      return Promise.resolve(undefined);
    },
    {
      // Deduplicate requests within 2 seconds (2000ms)
      dedupingInterval: 2000,
      // Disable automatic revalidation on window focus
      revalidateOnFocus: false,
      // Disable automatic revalidation on network reconnect
      revalidateOnReconnect: false,
      // Automatically refetch every 5 minutes
      refreshInterval: 300000,
      // Keep previous data when revalidating to maintain UI state
      keepPreviousData: true,
      // Retry on error with exponential backoff
      shouldRetryOnError: true,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
    },
  );

  // Combined loading state: loading if clients are loading OR distinct values are loading
  const isLoading = isLoadingClients || isLoadingDistinctValues;

  // Function to refresh distinct values after updates. Every cleaning action
  // also changes the field and entity counts, which come from a separate
  // request that otherwise only refreshes every 5 minutes, so revalidate it too.
  const refreshDistinctValues = useCallback(() => {
    mutate();
    mutateCache(CLEANING_DATA_KEY);
  }, [mutate, mutateCache]);

  // Update distinctValues when API data changes and apply manual moves
  useEffect(() => {
    const transformedData = transformDistinctValuesResponse(data);

    // Apply manual category moves to the fresh data
    if (transformedData.length > 0 && manualMoves.size > 0) {
      const updatedData = transformedData.map((val) => {
        const manualMove = manualMoves.get(val.value);
        if (manualMove) {
          // Apply the manual move
          return { ...val, categoryId: manualMove.categoryId };
        }
        return val;
      });

      // Clean up stale manual moves (values that no longer exist in the data)
      const validValues = new Set(transformedData.map((v) => v.value));
      const cleanedMoves = new Map(
        Array.from(manualMoves.entries()).filter(([value]) =>
          validValues.has(value),
        ),
      );

      if (cleanedMoves.size !== manualMoves.size) {
        setManualMoves(cleanedMoves);
      }

      setDistinctValues(updatedData);
    } else {
      setDistinctValues(transformedData);
    }
  }, [data, manualMoves]);

  // Calculate category counts and enriched category data
  const { categories } = useCategoryCalculations(distinctValues);

  const mainValues = useMemo(
    () => distinctValues.filter((val) => val?.categoryId === selectedCategory),
    [distinctValues, selectedCategory],
  );

  // Manage checkbox state
  const {
    checked,
    handleSelectAll,
    handleCheckValue,
    clearChecked,
    allChecked,
    indeterminate,
  } = useCheckboxState(mainValues, selectedCategory);

  const handleMoveValue = useCallback(
    (valueId: string, targetCategory: number) => {
      if (targetCategory !== undefined) {
        // Find the value string for this ID to track the move
        const valueItem = distinctValues.find((v) => v.id === valueId);
        if (valueItem) {
          // Track this manual move so it persists after data refresh
          // Use value string as key (not ID) since IDs change on refresh
          setManualMoves((prev) => {
            const updated = new Map(prev);
            updated.set(valueItem.value, {
              categoryId: targetCategory,
              timestamp: Date.now(),
            });
            return updated;
          });
        }

        // Update the local state immediately
        setDistinctValues((prev) =>
          prev.map((val) =>
            val.id === valueId ? { ...val, categoryId: targetCategory } : val,
          ),
        );
        clearChecked(valueId);
        setAnchorEl((prev) => ({ ...prev, [valueId]: null }));
      }
    },
    [clearChecked, distinctValues],
  );

  const handleMoveAllChecked = useCallback(
    (targetCategory: number) => {
      const checkedValueIds = Object.keys(checked).filter((id) => checked[id]);

      if (checkedValueIds.length === 0) {
        return;
      }

      // Track all manual moves in a single state update
      setManualMoves((prev) => {
        const updated = new Map(prev);
        checkedValueIds.forEach((valueId) => {
          const valueItem = distinctValues.find((v) => v.id === valueId);
          if (valueItem) {
            updated.set(valueItem.value, {
              categoryId: targetCategory,
              timestamp: Date.now(),
            });
          }
        });
        return updated;
      });

      // Update the local state immediately for all checked items
      setDistinctValues((prev) =>
        prev.map((val) =>
          checkedValueIds.includes(val.id)
            ? { ...val, categoryId: targetCategory }
            : val,
        ),
      );

      // Clear all checkboxes
      checkedValueIds.forEach((valueId) => {
        clearChecked(valueId);
      });
    },
    [checked, distinctValues, clearChecked],
  );

  const {
    dragOverCategory,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    getCategoryBackground,
  } = useDragDropHook({
    onMoveValue: handleMoveValue,
    onMoveAllChecked: handleMoveAllChecked,
    checked,
    selectedCategory,
  });

  const handleOpenMenu = useCallback(
    (valueId: string, event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl((prev) => ({ ...prev, [valueId]: event.currentTarget }));
    },
    [],
  );

  const handleCloseMenu = useCallback((valueId: string) => {
    setAnchorEl((prev) => ({ ...prev, [valueId]: null }));
  }, []);

  const handleItemClick = useCallback((item: DistinctValue) => {
    setSelectedItem((prevItem) => (prevItem?.id === item.id ? null : item));
  }, []);

  const handleOpenChart = useCallback((item: DistinctValue) => {
    setChartItem(item);
    setChartDrawerOpen(true);
  }, []);

  const handleCloseChart = useCallback(() => {
    setChartDrawerOpen(false);
    setChartItem(null);
  }, []);

  const selectedCategoryLabel = useMemo(
    () =>
      categories.find((cat) => cat.categoryId === selectedCategory)?.label ||
      "",
    [categories, selectedCategory],
  );

  return {
    // State
    distinctValues,
    checked,
    selectedCategory,
    selectedTile,
    selectedItem,
    anchorEl,
    categories,
    mainValues,
    selectedCategoryLabel,
    allChecked,
    indeterminate,
    dragOverCategory,
    isLoading,
    error,
    fieldId: selection.selectedFieldId,
    chartDrawerOpen,
    chartItem,

    // Setters
    setSelectedCategory,
    setSelectedTile,

    // Handlers
    handleMoveValue,
    handleMoveAllChecked,
    handleSelectAll,
    handleCheckValue,
    handleOpenMenu,
    handleCloseMenu,
    handleItemClick,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    getCategoryBackground,
    refreshDistinctValues,
    handleOpenChart,
    handleCloseChart,
  };
};
